import type { GeoLocationsList } from '@openx/types/geoLocation.ts';
import {
  GeographicOption,
  type GeographicTargetingState,
  type RadiusCircle,
} from '@openx/types/targeting/geographic.ts';
import pick from 'lodash/pick';
import { type ReactElement, useMemo } from 'react';
import { useCustomOptionsContext } from '../../../utils/hooks/useCustomOptionsContext.tsx';
import { BoundingBox } from '../GeographicTopBox/BoundingBox.tsx';
import { Location } from '../GeographicTopBox/Location.tsx';
import { LocationRadius } from '../GeographicTopBox/LocationRadius.tsx';
import { LocationSource } from '../GeographicTopBox/LocationSource.tsx';

type UseGeographicOptionsParams = {
  targetingParams: GeographicTargetingState;
  optionDropdown: ReactElement | null;
  onSelect: (item) => void;
  onBulkApplyPostalCode: (items: GeoLocationsList) => void;
  onBulkApplyCoordinates: (items: RadiusCircle[]) => void;
};

type UseGeographicOptionsReturn = {
  optionsTypes: Record<string, ReactElement<Record<string, unknown>>>;
  optionsNames: Record<string, string>;
};

export const useGeographicOptions = ({
  targetingParams,
  optionDropdown,
  onSelect,
  onBulkApplyPostalCode,
  onBulkApplyCoordinates,
}: UseGeographicOptionsParams): UseGeographicOptionsReturn => {
  const customOptions = useCustomOptionsContext<GeographicOption>();

  let optionsTypes: Record<string, ReactElement<Record<string, unknown>>> = useMemo(
    () => ({
      [GeographicOption.CIRCLES]: (
        <LocationRadius
          onSelect={onSelect}
          circles_v2={targetingParams.circles_v2}
          onBulkApply={onBulkApplyCoordinates}
        >
          {optionDropdown}
        </LocationRadius>
      ),
      [GeographicOption.INCLUDES]: (
        <Location targetingParams={targetingParams} onBulkApply={onBulkApplyPostalCode} onSelect={onSelect}>
          {optionDropdown}
        </Location>
      ),
      [GeographicOption.SOURCE]: (
        <LocationSource locationSourceState={targetingParams[GeographicOption.SOURCE]} onSelect={onSelect}>
          {optionDropdown}
        </LocationSource>
      ),
      [GeographicOption.BOUNDING_BOX]: (
        <BoundingBox boundingBoxState={targetingParams[GeographicOption.BOUNDING_BOX]} onChange={onSelect}>
          {optionDropdown}
        </BoundingBox>
      ),
    }),
    [onBulkApplyCoordinates, onBulkApplyPostalCode, onSelect, optionDropdown, targetingParams]
  );

  const optionsNames: Record<string, string> = useMemo(
    () => ({
      [GeographicOption.BOUNDING_BOX]: 'Bounding Box',
      [GeographicOption.INCLUDES]: 'Location',
      [GeographicOption.CIRCLES]: 'Location Radius',
      [GeographicOption.SOURCE]: 'Location Source',
    }),
    []
  );

  if (customOptions) {
    optionsTypes = pick(optionsTypes, customOptions.availableOptions);
  }

  return {
    optionsTypes,
    optionsNames,
  };
};
